/**
 * CA DER Charges Service
 *
 * Handles California Departing Load and Standby charge operations:
 * - Detection based on scenario/location
 * - Fetching charge rates
 * - Rate snapshots for optimization
 */

import { apiRequest } from './api';

/**
 * Run CA DER charge detection for a scenario
 * @param {Object} scenario - Scenario configuration data
 * @param {Object} location - { latitude, longitude }
 * @param {Object} currentUser - Firebase user for auth
 * @returns {Promise<Object>} Detection results
 */
export async function detectCADERCharges(scenario, location, currentUser = null) {
    const response = await apiRequest('/api/site_dna/v1/ca-der/detect', {
        method: 'POST',
        body: JSON.stringify({ scenario, location }),
    }, currentUser);

    if (!response.ok) {
        let errorDetail;
        try {
            const error = await response.json();
            errorDetail = error.error || error.message || error.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get charge rates for a utility
 * @param {string} utility - PGE, SCE, or SDGE
 * @param {Object} currentUser - Firebase user for auth
 * @returns {Promise<Object>} Charge rates
 */
export async function getChargeRates(utility, currentUser = null) {
    const params = new URLSearchParams();
    if (utility) params.append('utility', utility);

    const response = await apiRequest(`/api/site_dna/v1/ca-der/charge-rates?${params}`, {
        method: 'GET',
    }, currentUser);

    if (!response.ok) {
        let errorDetail;
        try {
            const error = await response.json();
            errorDetail = error.error || error.message || error.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get a snapshot of charge rates for the solver
 * @param {string} utility - PGE, SCE, or SDGE
 * @param {number} vintageYear - COD year for PCIA vintage
 * @param {Object} currentUser - Firebase user for auth
 * @returns {Promise<Object>} Rates snapshot
 */
export async function getRatesSnapshot(utility, vintageYear, currentUser = null) {
    const response = await apiRequest('/api/site_dna/v1/ca-der/charge-rates/snapshot', {
        method: 'POST',
        body: JSON.stringify({ utility, vintage_year: vintageYear }),
    }, currentUser);

    if (!response.ok) {
        let errorDetail;
        try {
            const error = await response.json();
            errorDetail = error.error || error.message || error.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Check if location is in California IOU territory
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {boolean} True if in CA IOU territory
 */
export function isCaliforniaIOU(lat, lon) {
    // Quick check for California bounds
    if (lat < 32.5 || lat > 42.0 || lon < -124.5 || lon > -114.0) {
        return false;
    }
    return true;
}

/**
 * Get utility name from code
 * @param {string} code - Utility code (PGE, SCE, SDGE)
 * @returns {string} Full utility name
 */
export function getUtilityName(code) {
    const names = {
        'PGE': 'Pacific Gas & Electric',
        'SCE': 'Southern California Edison',
        'SDGE': 'San Diego Gas & Electric'
    };
    return names[code] || code;
}

/**
 * Format currency for display
 * @param {number} value - Dollar amount
 * @returns {string} Formatted string
 */
export function formatDERCurrency(value) {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
}

export default {
    detectCADERCharges,
    getChargeRates,
    getRatesSnapshot,
    isCaliforniaIOU,
    getUtilityName,
    formatDERCurrency
};
